import { useEffect, useRef } from 'react';
import type { RefObject } from 'react';

/**
 * Lightweight scroll-reveal hook using IntersectionObserver.
 *
 * SSG-safe: elements start visible (no flash), then get the hidden class
 * client-side before IntersectionObserver reveals them on scroll.
 * Respects prefers-reduced-motion — skips all animation.
 */
export function useScrollReveal<T extends HTMLElement = HTMLElement>(
  options?: { threshold?: number; rootMargin?: string },
): RefObject<T> {
  const ref = useRef<T | null>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    el.classList.add('reveal--hidden');

    // Small delay to let the browser paint the hidden state before observing
    const raf = requestAnimationFrame(() => {
      const observer = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            el.classList.remove('reveal--hidden');
            el.classList.add('reveal--visible');
            observer.unobserve(el);
          }
        },
        {
          threshold: options?.threshold ?? 0.15,
          rootMargin: options?.rootMargin ?? '0px 0px -40px 0px',
        },
      );
      observer.observe(el);
    });

    return () => cancelAnimationFrame(raf);
  }, [options?.threshold, options?.rootMargin]);

  return ref as RefObject<T>;
}

/**
 * Observes all children matching `[data-reveal]` within a container.
 * Each child is revealed with a stagger delay based on its DOM index.
 */
export function useStaggerReveal<T extends HTMLElement = HTMLElement>(
  options?: { threshold?: number; staggerMs?: number; rootMargin?: string },
): RefObject<T> {
  const ref = useRef<T | null>(null);

  useEffect(() => {
    const container = ref.current;
    if (!container) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const children = container.querySelectorAll<HTMLElement>('[data-reveal]');
    if (!children.length) return;

    const stagger = options?.staggerMs ?? 100;

    children.forEach((child) => child.classList.add('reveal--hidden'));

    const raf = requestAnimationFrame(() => {
      const observer = new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            if (!entry.isIntersecting) continue;
            const el = entry.target as HTMLElement;
            const index = Array.from(children).indexOf(el);
            setTimeout(() => {
              el.classList.remove('reveal--hidden');
              el.classList.add('reveal--visible');
            }, index * stagger);
            observer.unobserve(el);
          }
        },
        {
          threshold: options?.threshold ?? 0.1,
          rootMargin: options?.rootMargin ?? '0px 0px -30px 0px',
        },
      );

      children.forEach((child) => observer.observe(child));
    });

    return () => cancelAnimationFrame(raf);
  }, [options?.threshold, options?.staggerMs, options?.rootMargin]);

  return ref as RefObject<T>;
}
